/**
 * タスクドメインスキーマ — エンティティ + CRUD + メッセージ + バージョン API
 */
import { z } from 'zod';
import { MessagePartSchema, ModelInfoSchema, TaskStatusSchema, UsageSchema } from './base-schemas';
import { TurnEndRecordSchema } from './task-end-schemas';
import { WebhookMetadataSchema } from './webhook-schemas';
// ========================================
// Todo進捗
// ========================================
/**
 * 項目の状態
 */
export const TodoItemStatusSchema = z.enum(['pending', 'running', 'completed', 'failed']);
/**
 * Todo項目
 */
export const TodoItemSchema = z.object({
    id: z.string(),
    description: z.string(),
    status: TodoItemStatusSchema,
});
/**
 * Todo進捗情報
 */
export const TodoProgressSchema = z.object({
    taskId: z.string(),
    todo: z
        .object({
        id: z.string(),
        items: z.array(TodoItemSchema),
    })
        .nullable(),
    startedAt: z.string(),
});
// ========================================
// エンティティ
// ========================================
/**
 * タスクメタデータ
 * taskId と version は別々のフィールド
 */
/**
 * スケジュール実行メタデータ
 */
export const ScheduleMetadataSchema = z.object({
    scheduleId: z.string(),
    scheduleName: z.string(),
    executionId: z.string(),
    scheduledAt: z.string(),
});
/**
 * タスクを実行した実行先。
 *
 * **記録するのはタスク開始時に解決された値**であり、あとから設定を切り替えても
 * 遡って変わらない。業務データがどこへ送られたかの記録であるため、
 * 後から書き換わってはならない（更新要求には含めない）。
 */
export const SandboxTargetSchema = z.enum(['local', 'cloud']);
export const TaskSchema = z.object({
    taskId: z.string(),
    version: z.number().int().positive(),
    userId: z.string(),
    title: z.string(),
    status: TaskStatusSchema,
    workspaceId: z.string().optional(),
    usage: UsageSchema.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().optional(),
    /** Todo履歴（複数Todo対応） */
    todoProgressHistory: z.array(TodoProgressSchema).optional(),
    /** スケジュール実行メタデータ */
    scheduleMetadata: ScheduleMetadataSchema.optional(),
    /** WebHook 起因タスクのメタデータ */
    webhookMetadata: WebhookMetadataSchema.optional(),
    /** どの実行先で実行されたか。クラウド実行先の導入前に作られたタスクでは未設定 */
    sandboxTarget: SandboxTargetSchema.optional() /** 直近ターンの終了記録（正の情報はメッセージ中の turn-end パートの列）。導入前のタスクでは未設定 */,
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
/**
 * タスクメッセージ
 * taskId と version は別々のフィールド
 */
export const TaskMessageSchema = z.object({
    messageId: z.string(),
    taskId: z.string(),
    version: z.number().int().positive(),
    sequenceNumber: z.number(),
    role: z.enum(['user', 'assistant', 'system']),
    content: z.union([z.string(), z.array(MessagePartSchema)]),
    model: ModelInfoSchema.optional(),
    usage: UsageSchema.optional(),
    duration: z.number().optional(),
    timestamp: z.string(),
});
// ========================================
// リクエスト
// ========================================
/**
 * POST /tasks リクエスト
 * taskId と version は別々のフィールドとして送信
 */
export const CreateTaskRequestSchema = z.object({
    taskId: z.string().optional(),
    /** バージョン番号（1から始まる正の整数、デフォルト: 1） */
    version: z.number().int().positive().optional(),
    title: z.string(),
    status: TaskStatusSchema.optional(),
    workspaceId: z.string().optional(),
    usage: UsageSchema.optional(),
    completedAt: z.string().optional(),
    /** Todo履歴（複数Todo対応） */
    todoProgressHistory: z.array(TodoProgressSchema).optional(),
    /** スケジュール実行メタデータ */
    scheduleMetadata: ScheduleMetadataSchema.optional(),
    /** WebHook 起因タスクのメタデータ */
    webhookMetadata: WebhookMetadataSchema.optional(),
    /** どの実行先で実行されたか（作成時点の解決結果） */
    sandboxTarget: SandboxTargetSchema.optional() /** 直近ターンの終了記録（正の情報はメッセージ中の turn-end パートの列）。導入前のタスクでは未設定 */,
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
/**
 * PUT /tasks/{taskId} リクエスト
 */
export const UpdateTaskRequestSchema = z.object({
    title: z.string().optional(),
    status: TaskStatusSchema.optional(),
    workspaceId: z.string().optional(),
    usage: UsageSchema.optional(),
    completedAt: z.string().optional(),
    /** Todo履歴（複数Todo対応） */
    todoProgressHistory: z.array(TodoProgressSchema).optional(),
    /**
     * どの実行先で実行されたか。
     *
     * **履歴再開のたびに更新できる。** これから走る実行の実行先は作成時の値ではなく
     * 現在の設定で決まるため、作成時の値のまま固定すると、ローカルで作ったタスクを
     * クラウドへ切り替えて再開したときに、業務データは実際に手元を離れているのに
     * 記録だけが「このパソコン」と主張する（手元の表示は更新され、管理画面だけが
     * 食い違う状態になる）
     */
    sandboxTarget: SandboxTargetSchema.optional() /** 直近ターンの終了記録。省略時は既存の値を変えない */,
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
/**
 * POST /tasks/{taskId}/messages リクエスト
 */
export const AddMessageRequestSchema = z.object({
    messageId: z.string().optional(),
    sequenceNumber: z.number(),
    role: z.enum(['user', 'assistant', 'system']),
    content: z.union([z.string(), z.array(MessagePartSchema)]),
    model: ModelInfoSchema.optional(),
    usage: UsageSchema.optional(),
    /** ターン実行時間（ミリ秒、assistant メッセージのみ） */
    duration: z.number().optional(),
    /**
     * クライアントがメッセージを生成した時刻（ISO 8601）
     *
     * クラウド保存はタスク終了時にまとめて実行されるため、サーバ受信時刻を採用すると
     * 全メッセージがタスク終了時刻に揃ってしまう（ユーザー発言の時刻がアシスタントの
     * 応答時刻になる）。実際の発言時刻を保つためクライアントの生成時刻を送る。
     * 未指定の場合はサーバ側で受信時刻を補完する（旧クライアント互換）。
     */
    timestamp: z.string().optional(),
});
/**
 * POST /tasks/delete リクエスト
 */
export const DeleteTasksRequestSchema = z.object({
    taskIds: z.array(z.string()).min(1, 'taskIds must have at least one element'),
});
/**
 * PATCH /tasks/workspace リクエスト
 */
export const UpdateTasksWorkspaceRequestSchema = z.object({
    taskIds: z.array(z.string()).min(1, 'taskIds must have at least one element'),
    workspaceId: z.string(),
});
// ========================================
// レスポンス
// ========================================
/**
 * GET /tasks レスポンス
 */
export const TaskListResponseSchema = z.object({
    tasks: z.array(TaskSchema),
    nextToken: z.string().optional(),
});
/**
 * GET /tasks/{taskId} レスポンス
 */
export const TaskDetailResponseSchema = z.object({
    task: TaskSchema,
    messages: z.array(TaskMessageSchema),
});
/**
 * POST /tasks レスポンス
 */
export const CreateTaskResponseSchema = z.object({
    taskId: z.string(),
    version: z.number(),
});
/**
 * POST /tasks/{taskId}/messages レスポンス
 */
export const AddMessageResponseSchema = z.object({
    messageId: z.string(),
});
/**
 * POST /tasks/delete レスポンス
 */
export const DeleteTasksResponseSchema = z.object({
    deleted: z.number(),
});
/**
 * PATCH /tasks/workspace レスポンス
 */
export const UpdateTasksWorkspaceResponseSchema = z.object({
    updated: z.number(),
});
/**
 * GET /tasks/{taskId}/messages レスポンス
 */
export const TaskMessagesResponseSchema = z.object({ messages: z.array(TaskMessageSchema) });
// ========================================
// バージョン
// ========================================
/**
 * バージョン情報
 */
export const TaskVersionInfoSchema = z.object({
    version: z.number().int().positive(),
    createdAt: z.string(),
    status: TaskStatusSchema,
    usage: UsageSchema.optional(),
});
/**
 * タスクサマリー（バージョン集約済み）
 */
export const TaskSummarySchema = z.object({
    taskId: z.string(),
    title: z.string(),
    status: TaskStatusSchema,
    latestVersion: z.number().int().positive(),
    versionCount: z.number().int().positive(),
    createdAt: z.string(),
    updatedAt: z.string(),
    usage: UsageSchema.optional(),
    totalUsage: UsageSchema.optional(),
    workspaceId: z.string().optional(),
    scheduleMetadata: ScheduleMetadataSchema.optional(),
    webhookMetadata: WebhookMetadataSchema.optional(),
});
/**
 * GET /tasks (バージョン集約) レスポンス
 */
export const TaskListAggregatedResponseSchema = z.object({
    tasks: z.array(TaskSummarySchema),
    nextToken: z.string().optional(),
});
/**
 * POST /tasks/{taskId}/versions リクエスト
 */
export const CreateVersionRequestSchema = z.object({
    /** コピー元のバージョン番号 */
    sourceVersion: z.number().int().positive(),
    /** コピー範囲（この index の直前のユーザーメッセージまで） */
    fromMessageIndex: z.number().int().min(0),
    /**
     * この版がどの実行先で実行されるか。
     *
     * **コピー元から引き継いではならない。** 再生成は「いまの設定で」動く。
     * コピー元の値を写すと、ローカルで作ったタスクをクラウドへ切り替えて
     * 再生成したとき、業務データは実際に手元を離れているのに監査の記録は
     * 「ローカル」と主張することになる。解決できないときは未設定のままにする
     * （推測して埋めるより空のほうが正しい）
     */
    sandboxTarget: SandboxTargetSchema.optional(),
});
/**
 * POST /tasks/{taskId}/versions レスポンス
 */
export const CreateVersionResponseSchema = z.object({
    taskId: z.string(),
    version: z.number().int().positive(),
    createdAt: z.string(),
});
/**
 * GET /tasks/{taskId}/versions レスポンス
 */
export const ListVersionsResponseSchema = z.object({
    versions: z.array(TaskVersionInfoSchema),
});
//# sourceMappingURL=task-schemas.js.map